import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Check, FileText, CreditCard, AlertCircle } from "lucide-react";
import confetti from "canvas-confetti";
import { toast } from "sonner";
import { api, formatApiError, SERVICE_LABELS } from "@/lib/api";
import { trackPageview, trackEvent } from "@/lib/analytics";

const fmtEur = (cents) =>
  (cents / 100).toLocaleString("de-DE", { style: "currency", currency: "EUR" });

// Konfetti-Feuerwerk in Markenfarben (Gold/Schwarz/Weiß)
const fireCelebration = () => {
  const colors = ["#d4af37", "#0a0a0a", "#ffffff", "#f1e2a5"];
  confetti({ particleCount: 90, spread: 75, origin: { y: 0.25 }, colors });
  setTimeout(() => confetti({ particleCount: 60, angle: 60, spread: 60, origin: { x: 0, y: 0.4 }, colors }), 250);
  setTimeout(() => confetti({ particleCount: 60, angle: 120, spread: 60, origin: { x: 1, y: 0.4 }, colors }), 450);
  setTimeout(() => confetti({ particleCount: 120, spread: 120, origin: { y: 0.3 }, colors, scalar: 0.8 }), 700);
};

export default function RechnungPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const [invoice, setInvoice] = useState(undefined);
  const [paying, setPaying] = useState(false);

  const load = () =>
    api
      .get(`/invoices/${id}/public`)
      .then(({ data }) => setInvoice(data))
      .catch(() => setInvoice(null));

  useEffect(() => {
    document.title = "Rechnung | JUSTIN HILFT";
    trackPageview(`/rechnung`);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (params.get("bezahlt") !== "1" || !invoice || invoice.status === "bezahlt") return;
    let tries = 0;
    const t = setInterval(async () => {
      tries += 1;
      await load();
      if (tries >= 15) clearInterval(t);
    }, 2000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, invoice?.status]);

  // Konfetti-Feier, sobald die Zahlung bestätigt ist (einmalig pro Seitenaufruf)
  useEffect(() => {
    if (params.get("bezahlt") === "1" && invoice?.status === "bezahlt") fireCelebration();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, invoice?.status]);

  const pay = async () => {
    trackEvent("bezahlen_click");
    setPaying(true);
    try {
      const { data } = await api.post(`/invoices/${id}/checkout`);
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err));
      setPaying(false);
    }
  };

  if (invoice === undefined) {
    return (
      <main className="min-h-screen bg-paper text-ink flex items-center justify-center" data-testid="rechnung-loading">
        <Loader2 size={24} className="animate-spin text-zinc-400" />
      </main>
    );
  }

  if (invoice === null) {
    return (
      <main className="min-h-screen bg-paper text-ink flex items-center justify-center px-6" data-testid="rechnung-not-found">
        <div className="text-center">
          <p className="font-display uppercase tracking-tight text-2xl mb-4">Rechnung nicht gefunden</p>
          <Link to="/" className="text-sm text-zinc-500 hover:text-ink">Zur Startseite</Link>
        </div>
      </main>
    );
  }

  const paid = invoice.status === "bezahlt";
  const overdue = invoice.status === "ueberfaellig";
  const justPaid = params.get("bezahlt") === "1";

  return (
    <main data-testid="rechnung-page" className="min-h-screen bg-paper text-ink px-6 lg:px-12 py-12">
      <div className="max-w-2xl mx-auto">
        <p className="font-display font-bold uppercase tracking-tight text-lg mb-1">
          Justin Hilft<span className="text-gold">.</span>
        </p>
        <p className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-10">Rechnung</p>

        {justPaid && (
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="border border-gold/40 bg-gold/5 p-8 lg:p-10 mb-8 text-center"
            data-testid="rechnung-danke-state"
          >
            <motion.span
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 260, damping: 16, delay: 0.2 }}
              className="mx-auto mb-6 w-16 h-16 rounded-full bg-gold flex items-center justify-center"
            >
              <Check size={30} className="text-ink" />
            </motion.span>
            {paid ? (
              <>
                <p className="font-display uppercase tracking-tight text-2xl lg:text-3xl mb-3">
                  Danke für deine Zahlung!
                </p>
                <p className="text-sm lg:text-base text-zinc-600 leading-relaxed max-w-md mx-auto">
                  Deine Zahlung über <strong className="text-ink">{fmtEur(invoice.amount_cents)}</strong> für
                  Rechnung <strong className="text-ink">{invoice.number}</strong> ist eingegangen.
                  Eine Bestätigung wurde an deine E-Mail-Adresse geschickt.
                </p>
              </>
            ) : (
              <>
                <p className="font-display uppercase tracking-tight text-2xl lg:text-3xl mb-3 flex items-center justify-center gap-3">
                  <Loader2 size={22} className="animate-spin text-gold" /> Zahlung wird bestätigt
                </p>
                <p className="text-sm lg:text-base text-zinc-600 leading-relaxed max-w-md mx-auto" data-testid="rechnung-processing-note">
                  Deine Zahlung wird gerade geprüft — das dauert nur wenige Sekunden. Die Seite aktualisiert sich automatisch.
                </p>
              </>
            )}
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <Link
                to="/"
                data-testid="rechnung-danke-home-button"
                className="bg-ink text-paper px-8 py-3.5 text-xs uppercase tracking-[0.25em] hover:bg-gold hover:text-ink transition-colors duration-300"
              >
                Zur Startseite
              </Link>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/invoices/${invoice.id}/pdf`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="rechnung-danke-pdf-button"
                className="border border-ink/20 px-8 py-3.5 text-xs uppercase tracking-[0.25em] hover:border-gold hover:text-gold transition-colors duration-300"
              >
                Rechnung als PDF
              </a>
            </div>
          </motion.div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="border border-black/10 bg-white p-8 lg:p-10 mb-8"
        >
          <div className="flex items-start justify-between gap-4 mb-8">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-zinc-500 mb-1">Rechnung</p>
              <p className="font-display uppercase tracking-tight text-2xl" data-testid="rechnung-number">{invoice.number}</p>
            </div>
            <FileText size={24} className="text-gold" />
          </div>

          <dl className="space-y-5 text-sm">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <dt className="text-xs uppercase tracking-[0.25em] text-zinc-500 mb-1">Rechnungsdatum</dt>
                <dd>{invoice.issued_at.slice(0, 10)}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-[0.25em] text-zinc-500 mb-1">Zahlungsfrist</dt>
                <dd className={overdue ? "text-red-600 font-medium" : ""}>{invoice.due_at.slice(0, 10)}</dd>
              </div>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-[0.25em] text-zinc-500 mb-1">Kunde</dt>
              <dd>{invoice.customer_name}{invoice.customer_address ? ` · ${invoice.customer_address}` : ""}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-[0.25em] text-zinc-500 mb-1">Leistung</dt>
              <dd>{SERVICE_LABELS[invoice.service] || invoice.service}</dd>
              <dd className="leading-relaxed text-zinc-600 whitespace-pre-wrap mt-1">{invoice.description}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-[0.25em] text-zinc-500 mb-1">Rechnungsbetrag</dt>
              <dd className="font-display text-3xl" data-testid="rechnung-amount">{fmtEur(invoice.amount_cents)}</dd>
              <dd className="text-xs text-zinc-500 mt-1">Gemäß § 19 UStG wird keine Umsatzsteuer berechnet (Kleinunternehmerregelung).</dd>
            </div>
          </dl>
        </motion.div>

        {paid ? (
          <div className="border border-emerald-600/30 bg-emerald-600/5 p-8 text-center" data-testid="rechnung-paid-state">
            <Check size={28} className="text-emerald-600 mx-auto mb-4" />
            <p className="font-display uppercase tracking-tight text-xl mb-2">Bezahlt</p>
            <p className="text-sm text-zinc-600">
              Diese Rechnung wurde bereits beglichen. Vielen Dank!
            </p>
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className="space-y-6"
          >
            {overdue && (
              <div className="border border-red-600/30 bg-red-600/5 p-5 flex items-start gap-4" data-testid="rechnung-overdue-note">
                <AlertCircle size={18} className="text-red-600 shrink-0 mt-0.5" />
                <p className="text-sm text-zinc-600">
                  Diese Rechnung ist überfällig. Bitte begleiche den offenen Betrag zeitnah.
                </p>
              </div>
            )}

            {invoice.stripe_available && (
              <button
                onClick={pay}
                disabled={paying}
                data-testid="rechnung-pay-button"
                className="group relative overflow-hidden w-full bg-gold text-ink py-5 text-xs uppercase tracking-[0.3em] font-medium shadow-[0_0_24px_rgba(212,175,55,0.35)] hover:shadow-[0_0_36px_rgba(212,175,55,0.55)] transition-shadow duration-500 disabled:opacity-70"
              >
                <span className="absolute inset-0 bg-goldlight translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]" />
                <span className="relative flex items-center justify-center gap-3">
                  {paying ? <Loader2 size={16} className="animate-spin" /> : <CreditCard size={16} />}
                  {paying ? "Weiter zur Zahlung ..." : `Jetzt online bezahlen — ${fmtEur(invoice.amount_cents)}`}
                </span>
              </button>
            )}

            <div className="border border-black/10 bg-white p-8">
              <p className="text-xs uppercase tracking-[0.25em] text-zinc-500 mb-4">Oder per Überweisung</p>
              <p className="text-sm text-zinc-600 leading-relaxed">
                Kontoinhaber: Justin Mochkaai<br />
                Bank: VR Bank Ostallgäu<br />
                IBAN: DE19 7209 0000 0002 7469 64<br />
                Verwendungszweck: {invoice.number}<br />
                Zahlbar bis zum {invoice.due_at.slice(0, 10)} ohne Abzug.
              </p>
            </div>
          </motion.div>
        )}

        <p className="mt-10 text-center">
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/invoices/${id}/pdf`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="rechnung-pdf-link"
            className="text-xs uppercase tracking-[0.25em] text-zinc-500 hover:text-ink transition-colors duration-300"
          >
            Rechnung als PDF herunterladen
          </a>
        </p>
      </div>
    </main>
  );
}
